/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/* Import components */
import CodeBlock from '@/components/code/CodeBlock';
import { useState } from 'react';

/* Import context functions */
import { useI18n } from '@/context/I18nContext';

/* Import configuration */
import {
    type SystemDashboardKpiStatusDefinition,
} from '@/lib/overview/systemDashboardKpiStatus';
import {
    resolveTimeSeriesTrafficLightLabelDefault,
    resolveTimeSeriesTrafficLightSeverityDefault,
    resolveTimeSeriesTrafficLightStatusLabel,
    type TimeSeriesTrafficLightSeverity,
} from '@/lib/timeSeries/timeSeriesValue';

interface SystemDashboardKpiStatusModalSource {
    sensorType: string;
    urn: string;
    value: string;
}

interface SystemDashboardKpiStatusModalProps {
    definition: SystemDashboardKpiStatusDefinition;
    displayValue: string;
    isDisplayValueTransformed: boolean;
    sources: readonly SystemDashboardKpiStatusModalSource[];
    status: number;
}

function getTrafficLightSeverities(
    definition: SystemDashboardKpiStatusDefinition,
): TimeSeriesTrafficLightSeverity[] {
    const severities = definition.trafficLight.rules.map((rule) => rule.severity);

    if (definition.trafficLight.fallback) {
        severities.push(definition.trafficLight.fallback);
    }

    return Array.from(new Set(severities));
}

function SystemDashboardKpiStatusModal({
    definition,
    displayValue,
    isDisplayValueTransformed,
    sources,
    status,
}: SystemDashboardKpiStatusModalProps) {
    const { t } = useI18n();
    const [activeTab, setActiveTab] = useState<'code' | 'sources' | 'status'>('sources');
    const codeBlockTheme = document.documentElement.getAttribute('data-theme') === 'dark'
        ? 'dark'
        : 'light';
    const trafficLightSeverity = resolveTimeSeriesTrafficLightSeverityDefault(
        status,
        definition.trafficLight,
    );
    const statusLabel = t(resolveTimeSeriesTrafficLightStatusLabel(status, definition.trafficLight));

    return (
        <div>
            <p className="text-sm font-medium text-base-content/80">
                <span className="text-base-content/60">{t('Current value')}:</span>{' '}
                <span className="text-base-content">{displayValue}</span>
                <span className="mx-2 text-base-content/30" aria-hidden="true">|</span>
                <span className="text-base-content/60">{t('State')}:</span>{' '}
                <span className="text-base-content">{statusLabel}</span>
                {isDisplayValueTransformed ? (
                    <>
                        <span className="mx-2 text-base-content/30" aria-hidden="true">|</span>
                        <span className="text-base-content/60">{t('Displayed value was converted.')}</span>
                    </>
                ) : null}
            </p>

            <div className="mt-8 layouts-code-tabs">
                <div className="tabs tabs-lift" role="tablist" aria-label={t('Status calculation')}>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'sources'}
                        className={`tab${activeTab === 'sources' ? ' tab-active' : ''}`}
                        onClick={() => setActiveTab('sources')}
                    >
                        {t('Source values')}
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'status'}
                        className={`tab${activeTab === 'status' ? ' tab-active' : ''}`}
                        onClick={() => setActiveTab('status')}
                    >
                        {t('Status calculation')}
                    </button>
                    {definition.calcCode ? (
                        <button
                            type="button"
                            role="tab"
                            aria-selected={activeTab === 'code'}
                            className={`tab${activeTab === 'code' ? ' tab-active' : ''}`}
                            onClick={() => setActiveTab('code')}
                        >
                            {t('Code')}
                        </button>
                    ) : null}
                </div>

                {activeTab === 'sources' ? (
                    <div className="layouts-code-surface overflow-x-auto">
                        <table className="table table-zebra font-sans">
                            <thead>
                                <tr>
                                    <th>{t('Sensor')}</th>
                                    <th>{t('Current value')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sources.map((source) => (
                                    <tr key={source.sensorType}>
                                        <td>
                                            <code>{source.urn}</code>
                                        </td>
                                        <td>{source.value}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : null}

                {activeTab === 'status' ? (
                    <div className="layouts-code-surface time-series-traffic-light-legend overflow-x-auto">
                        <table className="table table-zebra font-sans">
                            <thead>
                                <tr>
                                    <th>{t('State')}</th>
                                    <th>{t('Rule')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {getTrafficLightSeverities(definition).map((severity) => {
                                    const rule = definition.trafficLight.rules.find((item) => item.severity === severity);
                                    const isActiveRule = trafficLightSeverity === severity;
                                    const ruleLabel = t(resolveTimeSeriesTrafficLightLabelDefault(
                                        severity,
                                        definition.trafficLight,
                                    ));

                                    return (
                                        <tr
                                            key={severity}
                                            className={isActiveRule ? 'font-semibold' : undefined}
                                        >
                                            <td>
                                                <div className="inline-flex items-center gap-3">
                                                    <span className={`time-series-kpi__legend-item${
                                                        isActiveRule
                                                            ? ' time-series-kpi__legend-item--active'
                                                            : ''
                                                    }`}>
                                                        <span
                                                            className={`time-series-kpi__legend-swatch time-series-kpi__legend-swatch--${severity}`}
                                                            aria-hidden="true"
                                                        />
                                                    </span>
                                                    <span>{ruleLabel}</span>
                                                </div>
                                            </td>
                                            <td>{rule?.title ?? t('Otherwise')}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : null}

                {activeTab === 'code' && definition.calcCode ? (
                    <CodeBlock
                        code={definition.calcCode}
                        language="typescript"
                        theme={codeBlockTheme}
                        roundedTop={false}
                        wrapperClassName="mb-0"
                    />
                ) : null}
            </div>
        </div>
    );
}

export default SystemDashboardKpiStatusModal;
